import { AuthApiError } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CopRoutes } from "app/routes";
import * as userApi from "./userApi";
import {
  AuthenticationError,
  explicitLogoutQueryParamName,
  LoggedInUserConstructorArgs,
  originalUrlQueryParamName,
  redirectUrlQueryParamName,
  useAppStatus,
  useSecurityServiceCore,
  logout as apiLogout,
} from "@wexinc/wex-common-web";
import { COPLoggedInUser, CopPermissionIds, CopPermissions } from "./models";
import { Endpoints } from "services/api";
import { Env } from "util/environment-helper";

let baseUrl = window.location.origin;
if (Env.proxyPath) {
  baseUrl = `${window.location.origin}/${Env.proxyPath}`;
}

const idpQueryParamName = "idp";

export const hashTokenName = "#token=";

export interface SignInParams {
  username: string;
  password: string;
}

let isLoggingOut = false;

const useCopSecurityService = () => {
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const appStatus = useAppStatus();

  const {
    securityStore,
    dispatch,
    clearLoggedInUser,
    updatePermissions,
    getOktaAuthorizationData,
    updateCurrentUser,
    userOrgPermissions,
    userHasOrgPermission,
    userHasGlobalPermission,
    userHasSuperAdminPermissions,
  } = useSecurityServiceCore(
    (args: LoggedInUserConstructorArgs) => new COPLoggedInUser(args),
    Endpoints.apiRoot,
    CopPermissions,
    () => {
      isLoggingOut = false;
    },
  );

  function gotoLogin() {
    if (isLoggingOut) {
      return;
    }

    const redirectUrl = location.pathname;
    navigate(`${CopRoutes.loginRoot}?${originalUrlQueryParamName}=${encodeURIComponent(redirectUrl)}`);
  }

  async function logout() {
    await apiLogout(Endpoints.apiRoot).catch((e) => console.error("failed to logout ", e));

    isLoggingOut = true;
    await oktaAuth.revokeAccessToken();
    await oktaAuth.closeSession();
    clearLoggedInUser();
    navigate(`${CopRoutes.loginRoot}?${explicitLogoutQueryParamName}=1`);
    dispatch({
      type: "set-auth-status",
      payload: "not-authenticated",
    });
    isLoggingOut = false;
  }

  function wasLoggedOutExplicitly() {
    return searchParams.get(explicitLogoutQueryParamName) === "1";
  }

  async function loginViaPreferredIDP(email: string, redirectUri: string | undefined, originalUri: string) {
    const idp = await getPreferredIpd(email);

    await loginViaSpecifiedIDP(idp, redirectUri, originalUri, email);
  }

  function getSecurityTokenFromUrl() {
    return window.location.hash.split(hashTokenName)[1];
  }

  function getIdpID(email: string) {
    return userApi.getUserLoginInfo(email);
  }

  async function loginViaSpecifiedIDP(
    idp: string | undefined,
    redirectUri: string | undefined,
    originalUri: string,
    loginHint?: string,
  ) {
    await oktaAuth.signInWithRedirect({
      originalUri,
      idp,
      redirectUri,
      loginHint,
    });
  }

  async function loginViaUsernamePassword(params: SignInParams, originalUrl: string, redirectUrl?: string) {
    try {
      const transaction = await oktaAuth.signInWithCredentials(params);

      if (transaction.status === "SUCCESS") {
        await oktaAuth.signInWithRedirect({
          sessionToken: transaction.sessionToken,
          originalUri: originalUrl,
          redirectUri: redirectUrl,
        });
      } else {
        throw new Error("Unable to log in.");
      }
    } catch (error) {
      if (error instanceof AuthApiError) {
        switch (error.errorCode) {
          case "E0000004": // Authentication failed
            throw new AuthenticationError("Login failed.");
            break;
          default:
            throw error;
        }
      } else {
        throw error;
      }
    }
  }

  async function getPreferredIpd(email: string) {
    const idp = await userApi.getUserLoginInfo(email);
    return idp.identityProviderId ? idp.identityProviderId : undefined;
  }

  async function handleLoginCallback() {
    appStatus.setAppStatus("begin-authentication");

    try {
      await oktaAuth.handleLoginRedirect();
    } catch (error) {
      if (
        error instanceof Error &&
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        (!!error as any).errorCode !== "INTERNAL" &&
        error.message !== "Unable to parse a token from the url"
      ) {
        throw error;
      }
      // gobble gobble. New behavior/error from Okta where it throws an error parsing internally.
    }
  }

  function isLoggingInForPortalApp() {
    // Apps *inside* cop *use* cop to Log in. So those apps will send a "redirectUrl" querystring param.
    if (searchParams.get(redirectUrlQueryParamName)) {
      const callbackUrl = new URL(searchParams.get(redirectUrlQueryParamName)!);
      return !callbackUrl.pathname.startsWith(baseUrl);
    }
    return false;
  }

  function getOriginalUrl() {
    return searchParams.get(originalUrlQueryParamName) || baseUrl + CopRoutes.root;
  }

  // We only expect this querystring param to be sent by apps *in* COP (not when the app *is* COP).
  // So returning undefined (basically when the app is COP) we allow whatever is set in App.tsx
  function getRedirectUrl() {
    return searchParams.get(redirectUrlQueryParamName) || undefined;
  }

  function getSpecifiedIDP() {
    return searchParams.get(idpQueryParamName) || undefined;
  }

  function resetPasswordRequest(email: string) {
    return userApi.resetPasswordRequest(email);
  }

  function getPasswordResetRequestInfo(passwordResetId: number, resetToken: string): Promise<{ email: string }> {
    return userApi.getPasswordResetRequestInfo(passwordResetId, resetToken);
  }

  function resetPassword(passwordResetId: number, password: string, resetToken: string) {
    return userApi.resetPassword(passwordResetId, password, resetToken);
  }

  async function filterUserOrgPerms(organizationId: string, perms: CopPermissionIds[]): Promise<CopPermissionIds[]> {
    if (userHasSuperAdminPermissions()) {
      return perms;
    }
    const allowedPerms: CopPermissionIds[] = [];
    for (const p of perms) {
      // not the most efficient to query the api for each of the above,
      // but I'm out of time
      if (await userHasOrgPermission(organizationId, p)) {
        allowedPerms.push(p);
      }
    }
    return allowedPerms;
  }

  return {
    handleLoginCallback,
    isLoggingInForPortalApp,
    loginViaPreferredIDP,
    loginViaSpecifiedIDP,
    gotoLogin,
    getOriginalUrl,
    getRedirectUrl,
    getSpecifiedIDP,
    getOktaAuthorizationData,
    getIdpsOktaIDs: getIdpID,
    logout,
    securityStore,
    updatePermissions,
    wasLoggedOutExplicitly,
    loginViaUsernamePassword,
    getSecurityTokenFromUrl,
    resetPasswordRequest,
    getPasswordResetRequestInfo,
    resetPassword,
    updateCurrentUser,
    userOrgPermissions,
    userHasOrgPermission,
    userHasGlobalPermission,
    userHasSuperAdminPermissions,
    filterUserOrgPerms,
  };
};

export default useCopSecurityService;